import { formatPaise } from '@oneapp/gst';
import type { ReturnDraft } from '@oneapp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { businessApi, businessKeys } from '../business/api';
import { Sheet } from '../components/Sheet';
import { Button, Callout } from '../components/ui';
import { ApiError } from '../lib/api';
import { WalkInEmbed } from '../records/WalkInScreen';
import { periodLabel } from '../returns/labels';
import { parseSalesPath, type SalesView, salesApi, salesKeys } from './api';
import { CorrectList } from './CorrectList';
import { GstFreeForm } from './GstFreeForm';
import { NoteForm } from './NoteForm';
import { SaleForm } from './SaleForm';
import { SalesList } from './SalesList';

/**
 * The month's sales pages — all sales, add a sale, edit a sale — as one screen that moves between them.
 * Opened from the chat they sit in a sheet over it; on their own (a link) they're a page. `onFinish`
 * says how they were left: "review" after "Add and review" or "Done", "back" otherwise. A GSTR-1A's (gap 4b)
 * are its fixes: sales and notes added, and filed sales corrected (picked from the month's GSTR-1).
 */

export type SalesFinish = 'review' | 'back';

interface Screen {
  label: string;
  aside: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}

function useSalesScreen(
  returnId: string,
  view: SalesView,
  setView: (view: SalesView) => void,
  finish: (how: SalesFinish) => void,
) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formId = useId();
  const [busy, setBusy] = useState(false);
  const [changed, setChanged] = useState(false);
  const draftQuery = useQuery({
    queryKey: salesKeys.draft(returnId),
    queryFn: ({ signal }) => salesApi.draft(returnId, signal),
  });
  const businesses = useQuery({ queryKey: businessKeys.all, queryFn: ({ signal }) => businessApi.list(signal) });
  // A sale to change, or (gap 4) a note to change.
  const invoiceId = view.kind === 'edit' ? view.invoiceId : view.kind === 'note' ? (view.invoiceId ?? '') : '';
  const invoiceQuery = useQuery({
    queryKey: salesKeys.invoice(returnId, invoiceId),
    queryFn: ({ signal }) => salesApi.invoice(returnId, invoiceId, signal),
    enabled: invoiceId !== '',
    staleTime: 0,
  });
  const draft = draftQuery.data?.return;
  const seller = businesses.data?.businesses.find((b) => b.id === draft?.businessId);
  // Gap 4b: a GSTR-1A, its fixes and what they do to GSTR-3B; and the filed sale being corrected.
  const g1a = draft?.form === 'gstr1a';
  const fixesQuery = useQuery({
    queryKey: salesKeys.gstr1a(returnId),
    queryFn: ({ signal }) => salesApi.gstr1a(returnId, signal),
    enabled: g1a,
  });
  const fixes = fixesQuery.data;
  const gstr1ReturnId = fixes?.gstr1ReturnId ?? '';
  const corrections = (fixes?.changes ?? []).filter((c) => c.kind === 'corrected').map((c) => c.invoice);
  const filedId = view.kind === 'correctOne' ? view.invoiceId : '';
  const originalQuery = useQuery({
    queryKey: salesKeys.invoice(gstr1ReturnId, filedId),
    queryFn: ({ signal }) => salesApi.invoice(gstr1ReturnId, filedId, signal),
    enabled: filedId !== '' && gstr1ReturnId !== '',
  });

  const updated = useCallback(
    (next: ReturnDraft) => {
      queryClient.setQueryData(salesKeys.draft(returnId), { return: next });
      void queryClient.invalidateQueries({ queryKey: salesKeys.all(returnId) });
      setChanged(true);
    },
    [queryClient, returnId],
  );

  const period = draft ? periodLabel(t, draft.period, 'month') : '';
  const month = draft ? t(`filings.months.${Number(draft.period.slice(5, 7))}`) : '';
  const screen: Screen = {
    label: period ? t(g1a ? 'sales.g1a.period' : 'sales.period', { period }) : t('sales.loading'),
    aside: null,
    body: null,
    footer: null,
  };
  const notFound = draftQuery.error instanceof ApiError && draftQuery.error.status === 404;

  if (
    draftQuery.isPending ||
    businesses.isPending ||
    (invoiceId && invoiceQuery.isPending) ||
    (g1a && fixesQuery.isPending) ||
    (filedId && gstr1ReturnId && originalQuery.isPending)
  ) {
    screen.body = <p className="muted">{t('sales.loading')}</p>;
    return { screen, changed };
  }
  if (!draft || !seller || (invoiceId && !invoiceQuery.data) || (g1a && !fixes)) {
    screen.body = (
      <Callout tone="error" icon="alert-triangle">
        {notFound ? t('sales.notFound') : t('sales.loadFailed')}
      </Callout>
    );
    return { screen, changed };
  }

  if (view.kind === 'list') {
    const effect = fixes?.effect;
    screen.body = (
      <SalesList
        returnId={returnId}
        draft={draft}
        periodName={month}
        onOpen={(invoice) =>
          setView(
            invoice.amends
              ? { kind: 'correctOne', invoiceId: invoice.amends }
              : invoice.source === 'walk_in'
                ? { kind: 'walkIn' }
                : invoice.docType !== 'invoice'
                  ? { kind: 'note', invoiceId: invoice.id }
                  : { kind: 'edit', invoiceId: invoice.id },
          )
        }
        onAddNote={() => setView({ kind: 'note' })}
        {...(g1a && gstr1ReturnId ? { onCorrect: () => setView({ kind: 'correct' }) } : {})}
        {...(g1a && effect
          ? {
              heading: t('sales.g1a.listTitle', { month }),
              summary: t('sales.g1a.effect', {
                month,
                before: formatPaise(effect.before.tax),
                after: formatPaise(effect.after.tax),
              }),
            }
          : {})}
        onResolved={updated}
      />
    );
    screen.footer = (
      <div className="sheet__buttons">
        <Button variant="secondary" icon="plus" onClick={() => setView({ kind: 'new' })}>
          {t('sales.list.add')}
        </Button>
        <Button onClick={() => finish('review')}>{t('sales.list.done')}</Button>
      </div>
    );
    return { screen, changed };
  }

  if (view.kind === 'walkIn') {
    screen.body = (
      <WalkInEmbed
        returnId={returnId}
        onDone={() => {
          setChanged(true);
          setView({ kind: 'list' });
        }}
      />
    );
    screen.footer = (
      <div className="sheet__buttons">
        <Button variant="secondary" onClick={() => setView({ kind: 'list' })}>
          {t('sales.form.allSales')}
        </Button>
      </div>
    );
    return { screen, changed };
  }

  // Gap 4: a credit or debit note (K2): a new one goes back to the chat once saved; a changed one to the list.
  if (view.kind === 'note') {
    const note = view.invoiceId ? invoiceQuery.data?.invoice : undefined;
    screen.aside = <span className="pill">{t(note ? 'sales.form.editing' : 'sales.note.newPill')}</span>;
    screen.body = (
      <NoteForm
        key={note ? `${note.id}-${note.version}` : `new-${view.against ?? ''}-${view.docType ?? ''}`}
        returnId={returnId}
        draft={draft}
        sellerGstin={seller.gstin}
        {...(note ? { note } : {})}
        {...(view.against ? { against: view.against } : {})}
        {...(view.docType ? { docType: view.docType } : {})}
        formId={formId}
        onBusy={setBusy}
        onSaved={(result) => {
          updated(result.draft);
          if (note) setView({ kind: 'list' });
          else finish('review');
        }}
        onDeleted={(next) => {
          updated(next);
          setView({ kind: 'list' });
        }}
      />
    );
    screen.footer = (
      <div className="sheet__buttons">
        <Button variant="secondary" onClick={() => setView({ kind: 'list' })} disabled={busy}>
          {t('sales.form.allSales')}
        </Button>
        <Button type="submit" form={formId} busy={busy}>
          {t('sales.note.save')}
        </Button>
      </div>
    );
    return { screen, changed };
  }

  // Gap 4: the month's GST-free sales (K6), then on to the review.
  if (view.kind === 'gstFree') {
    const nothing = async () => {
      setBusy(true);
      try {
        updated((await salesApi.putGstFree(returnId, { rows: [] })).return);
        finish('review');
      } finally {
        setBusy(false);
      }
    };
    screen.body = (
      <GstFreeForm
        returnId={returnId}
        draft={draft}
        sellerGstin={seller.gstin}
        formId={formId}
        onBusy={setBusy}
        onSaved={(next) => {
          updated(next);
          finish('review');
        }}
      />
    );
    screen.footer = (
      <div className="sheet__buttons">
        <Button variant="secondary" onClick={nothing} disabled={busy}>
          {t('sales.gstFree.nothing')}
        </Button>
        <Button type="submit" form={formId} busy={busy}>
          {t('sales.gstFree.save')}
        </Button>
      </div>
    );
    return { screen, changed };
  }

  // Gap 4b: the month's GSTR-1 as filed, to pick a sale to correct (K8).
  if (view.kind === 'correct') {
    screen.body = gstr1ReturnId ? (
      <CorrectList
        gstr1ReturnId={gstr1ReturnId}
        periodName={month}
        corrected={new Set(corrections.map((c) => c.amends ?? ''))}
        onPick={(id) => setView({ kind: 'correctOne', invoiceId: id })}
      />
    ) : (
      <Callout tone="amber" icon="alert-triangle">
        {t('sales.correct.elsewhere')}
      </Callout>
    );
    screen.footer = (
      <div className="sheet__buttons">
        <Button variant="secondary" onClick={() => setView({ kind: 'list' })}>
          {t('sales.g1a.allFixes')}
        </Button>
      </div>
    );
    return { screen, changed };
  }

  // Gap 4b: a filed sale corrected: its value, rate, date or place; then another, or the review (K9).
  if (view.kind === 'correctOne') {
    const original = originalQuery.data?.invoice;
    if (!gstr1ReturnId || !original) {
      screen.body = (
        <Callout tone={gstr1ReturnId ? 'error' : 'amber'} icon="alert-triangle">
          {t(gstr1ReturnId ? 'sales.loadFailed' : 'sales.correct.elsewhere')}
        </Callout>
      );
      return { screen, changed };
    }
    const existing = corrections.find((c) => c.amends === original.id);
    screen.aside = <span className="pill">{t('sales.correct.pill')}</span>;
    screen.body = (
      <SaleForm
        key={`${original.id}-${existing?.version ?? 0}`}
        returnId={returnId}
        draft={draft}
        sellerGstin={seller.gstin}
        correcting={original}
        {...(existing ? { invoice: existing } : {})}
        formId={formId}
        onBusy={setBusy}
        onSaved={(result, how) => {
          updated(result.draft);
          if (how === 'another') setView({ kind: 'correct' });
          else finish('review');
        }}
        onDeleted={(next) => {
          updated(next);
          setView({ kind: 'list' });
        }}
      />
    );
    screen.footer = (
      <div className="sheet__buttons">
        <Button variant="secondary" type="submit" form={formId} name="how" value="another" disabled={busy}>
          {t('sales.correct.another')}
        </Button>
        <Button type="submit" form={formId} name="how" value="review" busy={busy}>
          {t('sales.correct.saveAndReview')}
        </Button>
      </div>
    );
    return { screen, changed };
  }

  if (view.kind === 'new') {
    screen.aside = <span className="pill">{t('sales.added', { count: draft.counts.all })}</span>;
    screen.body = (
      <SaleForm
        key="new"
        returnId={returnId}
        draft={draft}
        sellerGstin={seller.gstin}
        formId={formId}
        onBusy={setBusy}
        onSaved={(result, how) => {
          updated(result.draft);
          if (how === 'review') finish('review');
        }}
      />
    );
    screen.footer = (
      <div className="sheet__buttons">
        <Button variant="secondary" type="submit" form={formId} name="how" value="another" disabled={busy}>
          {t('sales.form.addAnother')}
        </Button>
        <Button type="submit" form={formId} name="how" value="review" busy={busy}>
          {t('sales.form.addAndReview')}
        </Button>
      </div>
    );
    return { screen, changed };
  }

  const invoice = invoiceQuery.data?.invoice;
  screen.aside = <span className="pill">{t('sales.form.editing')}</span>;
  screen.body = invoice ? (
    <SaleForm
      key={`${invoice.id}-${invoice.version}`}
      returnId={returnId}
      draft={draft}
      sellerGstin={seller.gstin}
      invoice={invoice}
      formId={formId}
      onBusy={setBusy}
      onSaved={(result) => {
        updated(result.draft);
        setView({ kind: 'list' });
      }}
      onDeleted={(next) => {
        updated(next);
        setView({ kind: 'list' });
      }}
    />
  ) : null;
  screen.footer = (
    <div className="sheet__buttons">
      <Button variant="secondary" onClick={() => setView({ kind: 'list' })} disabled={busy}>
        {t('sales.form.allSales')}
      </Button>
      <Button type="submit" form={formId} busy={busy}>
        {t('sales.form.saveChanges')}
      </Button>
    </div>
  );
  return { screen, changed };
}

/** Opened from the chat (a "page" widget): the sheet over it. */
export function SalesSheet({
  path,
  onClose,
  onFinish,
}: {
  path: string;
  /** Closed without changing anything. */
  onClose: () => void;
  onFinish: (how: SalesFinish) => void;
}) {
  const parsed = parseSalesPath(path);
  const [view, setView] = useState<SalesView>(parsed?.view ?? { kind: 'list' });
  const { screen, changed } = useSalesScreen(parsed?.returnId ?? '', view, setView, onFinish);
  const close = () => (changed ? onFinish('back') : onClose());
  return (
    <Sheet open onClose={close} label={screen.label} aside={screen.aside} footer={screen.footer}>
      {screen.body}
    </Sheet>
  );
}

/** The same screens on their own, for a link to them. */
export function SalesStandalone({
  returnId,
  view,
  onView,
  onFinish,
  frame,
}: {
  returnId: string;
  view: SalesView;
  onView: (view: SalesView) => void;
  onFinish: (how: SalesFinish) => void;
  frame: (screen: Screen) => ReactNode;
}) {
  const { screen } = useSalesScreen(returnId, view, onView, onFinish);
  return <>{frame(screen)}</>;
}
